import { useState, useEffect, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import type { User } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { AuthContext } from "@/lib/contexts/AuthContext";

// Firebase's persistence read (IndexedDB) can transiently fail on a slow or
// backgrounded tab, a device waking from sleep, or Safari's IndexedDB
// throttling — with no public API to force a re-check within the same page
// load. A single reload gives Firebase a fresh persistence read, which
// resolves the vast majority of these false "signed out" results.
const RELOAD_GUARD_KEY = "disca-admin-auth-reload-guard";

// undefined = loading, null = unauthenticated, User = authenticated
export function useAdminAuth(): User | null | undefined {
  // Inside ProtectedShell the context always holds the confirmed user —
  // return it directly so no component ever independently re-resolves auth.
  const contextUser = useContext(AuthContext);

  // Own state: used only outside ProtectedShell (LoginPage, ProtectedShell itself).
  // Sync init from auth.currentUser so components that mount after auth is already
  // resolved get the user on first render without waiting for an async tick.
  // null → undefined so the loading state is preserved on initial page load.
  const [user, setUser] = useState<User | null | undefined>(() =>
    contextUser !== undefined ? contextUser : (auth.currentUser ?? undefined)
  );

  useEffect(() => {
    // Skip own auth resolution when context provides the user.
    if (contextUser !== undefined) return;

    // Use onAuthStateChanged (not authStateReady + auth.currentUser) so the
    // initial state is determined after Firebase finishes reading persistence.
    // authStateReady() can race against the persistence layer and resolve with
    // a null currentUser even when a valid session exists in IndexedDB.
    let initialised = false;
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!initialised) {
        initialised = true;
        if (firebaseUser === null && !sessionStorage.getItem(RELOAD_GUARD_KEY)) {
          // First-ever null this tab has seen: could be a genuine signed-out
          // state or a flaky persistence read. Reload once (resets Firebase's
          // persistence check) before trusting it; the guard prevents a loop
          // if the reload confirms the user really is signed out.
          sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
          window.location.reload();
          return;
        }
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
        setUser(firebaseUser);
      } else if (firebaseUser !== null) {
        // Mid-session: only update on sign-in; ignore null to prevent spurious
        // logouts during token refresh. Real sign-out goes via Sidebar.
        setUser(firebaseUser);
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return contextUser !== undefined ? contextUser : user;
}
